import Box from '@mui/material/Box';
import { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { usePageImageLightbox } from 'src/common/components/PageImageLightboxContext';
import {
  imageDisplayOptionsToClassName,
  parseImageBlockLayerSx,
  parseStackedGalleryContainerSx,
} from 'src/helpers/pageBlockImageUtils';
import { IImageBlockDisplayOptions } from 'src/interfaces/PageBlock';

const STACK_ROTATIONS = [5, -7, 4, -5];
const MAX_VISIBLE_LAYERS = 4;

interface StackedGalleryImage {
  src: string;
  alt?: string;
}

interface Props {
  images: StackedGalleryImage[];
  display?: IImageBlockDisplayOptions;
  interactive?: boolean;
}

export default function StackedGalleryThumbnail({
  images,
  display,
  interactive = true,
}: Props) {
  const { t } = useTranslation();
  const lightbox = usePageImageLightbox();
  const isLightboxEnabled = interactive && lightbox != null && lightbox.images.length > 0;

  const validImages = images.filter(image => image.src?.trim());
  if (validImages.length === 0) return null;

  const frontImage = validImages[0]!;
  const backImages = validImages.slice(1, MAX_VISIBLE_LAYERS + 1).reverse();
  const imageClassName = imageDisplayOptionsToClassName(display);

  const handleOpen = () => {
    if (!isLightboxEnabled || !frontImage) return;
    lightbox?.openAtSrc(frontImage.src);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!isLightboxEnabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpen();
    }
  };

  const stackCount = validImages.length;
  const ariaLabel = t('system.pages.imageBlock.openGallery', { count: stackCount });

  const layerSx = (extra: Record<string, unknown> = {}) => [
    parseImageBlockLayerSx(display),
    () => extra,
  ];

  return (
    <Box sx={parseStackedGalleryContainerSx(display)}>
      <Box
        role={interactive ? 'button' : undefined}
        tabIndex={interactive && isLightboxEnabled ? 0 : undefined}
        onClick={isLightboxEnabled ? handleOpen : undefined}
        onKeyDown={isLightboxEnabled ? handleKeyDown : undefined}
        aria-label={interactive ? ariaLabel : undefined}
        sx={{
          position: 'absolute',
          inset: 0,
          border: 'none',
          padding: 0,
          margin: 0,
          background: 'transparent',
          cursor: isLightboxEnabled ? 'zoom-in' : 'default',
          width: '100%',
          height: '100%',
          '&:focus-visible': isLightboxEnabled
            ? {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              }
            : {},
          '&:hover .stack-layer': {
            transition: 'transform 0.2s ease',
          },
          '&:hover .stack-layer-0': { transform: 'rotate(7deg) translateY(4px)' },
          '&:hover .stack-layer-1': { transform: 'rotate(-9deg) translateY(2px)' },
          '&:hover .stack-layer-2': { transform: 'rotate(6deg) translateY(-2px)' },
          '&:hover .stack-layer-3': { transform: 'rotate(-6deg) translateY(-4px)' },
        }}
      >
        {backImages.map((image, index) => {
          const layerIndex = backImages.length - 1 - index;
          const rotation = STACK_ROTATIONS[layerIndex % STACK_ROTATIONS.length];
          return (
            <Box
              key={`${image.src}-back-${index}`}
              aria-hidden
              className={`stack-layer stack-layer-${layerIndex} ${imageClassName}`}
              component="img"
              src={image.src}
              alt=""
              sx={layerSx({
                transform: `rotate(${rotation}deg)`,
                zIndex: index + 1,
                opacity: 0.92,
              })}
            />
          );
        })}
        <Box
          component="img"
          className={imageClassName}
          src={frontImage.src}
          alt={frontImage.alt ?? ''}
          sx={layerSx({ zIndex: backImages.length + 2 })}
        />
      </Box>
    </Box>
  );
}
